import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { PersonSearchComponent } from '../shared/person-search/person-search.component';
import { ApprovalRouteComponent } from '../manage-request/approval-route/approval-route.component';
import { RequestHistoryComponent } from '../manage-request/request-history/request-history.component';
import { MatTabsModule } from '@angular/material/tabs';
import { EnterRequestService } from './services/enter-request.service';
import { LoggedInUserService } from '../services/common/logged-in-user.service';
import { ILoggedInUser } from '../interfaces/common/loggedInUser';
import { IPersonComplete } from '../models/person.model';
import { forkJoin } from 'rxjs';
import { IRequestType } from './models/request-type.model';
import { IPerson } from '../models/person.model';
import { ILineType } from '../administration/models/line-type.model';
import { ILineTypeTable } from './models/line-types-table.model';
import { toWords } from 'number-to-words';
import { SearchAddressBookEntryComponent } from '../shared/search-address-book-entry/search-address-book-entry.component';
import { AddressBookEntry } from '../shared/models/address-book.model';
import { AddressBookSearchService } from '../services/common/address-book-search.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchRequestService } from '../manage-request/services/search-request.service';
import { ImprestDetails, IRequest } from '../manage-request/models/request.model';
import { NextPage } from '@cvx/nextpage';
import { IApprovalRoute } from '../manage-request/models/approval-route.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IEnterRequest } from './models/enter-request.model';
import { MatIconModule } from '@angular/material/icon'
import { EnterRequestAction } from './enums/enter-request-action.enum';
import { IRequestTerminate } from '../manage-request/models/request-terminate.model';
import { EnterRequestResponse } from './models/enter-request-response.model';
import { BlobModel } from '../manage-request/models/blob.model';
import { DistanceService } from '../administration/services/distance.service';
import { SharedService } from '../services/common/shared.service';
import { IBusinessUnit } from '../models/business-unit.model';

export interface PeriodicElement {
  serialNumber: number;
  lineType: string;
  description: string;
  amount: number;
}

@Component({
  selector: 'app-enter-request',
  standalone: true,
  imports: [CommonModule,
    MatAutocompleteModule,
    ApprovalRouteComponent,
    MatTabsModule,
    RequestHistoryComponent, NextPage, MatProgressSpinnerModule,
    MatTableModule, ReactiveFormsModule, CommonModule, PersonSearchComponent, FormsModule, MatIconModule],
  templateUrl: './enter-request.component.html',
  styleUrl: './enter-request.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EnterRequestComponent implements OnInit {
  ACTION = EnterRequestAction;
  user: ILoggedInUser;
  userRole: string;
  isEdit: boolean = false;
  requestSubmitted: boolean = false;
  submissionInProgress: boolean = true;
  personComplete: IPersonComplete = {
    id: 0,
    firstName: '',
    middleName: '',
    surName: '',
    email: '',
    fullName: '',
    reportsToName: '',
    reportsToEmail: '',
    employeeNo: 0,
    employeeType: '',
    jobTitle: '',
    costCenter: '',
    division: '',
    location: '',
    employeeTypeID: 0,
    atmCardVerified: false
  }
  requestTypes: IRequestType[];
  requestTypesSource: IRequestType[];
  lineTypes: ILineType[];
  comment: string = '';
  lineTypesTableData: ILineTypeTable[];
  totalAmount: number = 0;
  isLoading: boolean = true;
  onBehalfOfName: string;
  onBehalfOfEmail: string;
  displayedColumns: string[] = ['serialNumber', 'lineType', 'description', 'amount', 'close'];
  lineTypesTableDataSource: MatTableDataSource<any>;
  lineTypeForm: FormGroup;
  requestForm: FormGroup;
  totalAmountWords: string = '';
  approver: IPerson = {
    email: '',
    fullName: '',
    firstName: '',
    surName: ''
  };
  selectedFiles: File[] = [];
  successResponse: string;
  validationErrors: string[];
  errorResponse: string;
  action: EnterRequestAction = EnterRequestAction.None;
  payableTo: AddressBookEntry = {
    name: '',
    currency: '',
    holdPayment: false,
    serialNumber: 0
  }
  requestId: number;
  requestData: IRequest;
  enterRequestResponse: EnterRequestResponse;
  imprestDetails: ImprestDetails[];
  activeTab: string = 'approvalRoute';
  historyDataLoaded: Boolean = false;
  approvalRouteData: IApprovalRoute[];
  isLineTypeLoading: boolean = false;
  townsFrom: string[];
  selectedChargeAfeCc: IBusinessUnit;

  @ViewChild(RequestHistoryComponent) requestHistoryComponent: RequestHistoryComponent;
  constructor(private userService: LoggedInUserService,
    private enterRequestService: EnterRequestService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private addressBookSearchService: AddressBookSearchService,
    private route: ActivatedRoute,
    private searchRequestService: SearchRequestService,
    private distanceService: DistanceService,
    private sharedService: SharedService,
    private router: Router) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.requestId = +params.get('id');

      if (this.requestId > 0) {
        this.isEdit = true;
        this.getEditData();
      }
      else {
        this.requestId = 0
      }
    });

    this.lineTypeForm = this.fb.group({
      lineType: ['', Validators.required],
      description: [''],
      amount: [''],
      limit: [''],
      rate: [''],
      factor: [''],
      promptType: [''],
      promptText1: [''],
      promptText2: [''],
      promptText3: [''],
      from: [''],
      to: [''],
      townsTo: [[]]
    });

    this.requestForm = this.fb.group({
      requestTitle: ["", Validators.required],
      requestType: ["", Validators.required],
      mode: ["D", Validators.required],
      chargeAfeCc: ["", Validators.required],
      inRegardOf: ["", Validators.required],
      totalAmount: [0],
      lineTypes: this.fb.array([])
    });

    this.lineTypesTableDataSource = new MatTableDataSource((this.requestForm.get('lineTypes') as FormArray).controls);

    this.userService.currentUser$.subscribe(data => {
      this.user = data;
      if (this.user !== null && this.user.userRole !== null) {
        this.userRole = this.user.userRole;

        if (!this.isEdit) {
          this.onBehalfOfEmail = this.user.userEmail;
          this.loadDataForNewRequest();
        }
      } else if (this.user !== null && this.user.userRole === null) {
        this.router.navigate(["/unauthorized"]);
        return;
      }
    });
  }

  onKeyUpChargeAfeCC(event: any) {
    this.getBusinessUnit(event.target.value.toUpperCase());
  }

  getBusinessUnit(mcu: string): void {
    this.sharedService.getBusinessUnit(mcu).subscribe({
      next: (data: IBusinessUnit) => {
        if (data) {
          this.selectedChargeAfeCc = data;
        }
      }});
  }

  convertToFiles(data: BlobModel[]) {
    data.forEach(item => {
      const byteArray = Uint8Array.from(atob(item.content.toString()), c => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/octet-stream' });
      const file = new File([blob], item.fileName.split('/').pop(), { type: 'application/octet-stream' });

      this.selectedFiles.push(file);
    });
  }

  getEditData() {
    this.isLoading = true;
    this.searchRequestService.getRequestDetails(this.requestId.toString()).subscribe({
      next: (data) => {
        this.requestData = data;

        this.searchRequestService.getAttachments(this.requestData.attachmentID).subscribe({
          next: (files) => {
            this.convertToFiles(files);
          }
        });

        this.imprestDetails = this.requestData.imprestDetails;
        if (this.requestData.numericStatus !== 0 && this.requestData.numericStatus !== 5) {
          this.isEdit = false
          this.isLoading = false;
          return;
        }
        this.totalAmount = this.requestData.totalAmountNumber;
        this.onBehalfOfSelected({
          email: this.requestData.employeeEmail,
          fullName: this.requestData.employeeName,
          firstName: '',
          surName: ''
        });
        this.patchValues();

        this.searchRequestService.loadApprovalRouteData(this.requestData.requestNumber).subscribe(data => {
          if (data) {
            this.approvalRouteData = data;
            this.approver.email = data[0]?.approverEmail;
            this.approver.fullName = data[0]?.approverName;
          }
          this.isLoading = false;
        });
      },
      error: (error) => {

      }
    })
  }

  setActiveTab(tabName: string): void {
    this.activeTab = tabName;
    if (tabName === "requestHistory" && this.historyDataLoaded === false) {
      this.historyDataLoaded = true;
      this.requestHistoryComponent.loadData();
    }
  }

  patchValues() {
    this.requestForm.patchValue({
      requestTitle: this.requestData.title,
      requestType: this.requestData.requestTypeId,
      mode: this.requestData.modeCode,
      chargeAfeCc: this.requestData.chargeAfeCc,
      inRegardOf: this.requestData.inRegardOf
    })
  }

  getAllTowns() {
    this.distanceService.getTowns().subscribe({
      next: (data) => {
        this.townsFrom = data
      }
    })
  }

  townFromChange(event: string, i: any) {
    if (event === '') return;
    this.distanceService.getTowns(event).subscribe({
      next: (data) => {
        this.lineItems.controls[i].patchValue({ townsTo: data });
        this.lineItems.controls[i].patchValue({ to: '' });
        this.lineItems.controls[i].patchValue({ factor: '' });
        this.lineItems.controls[i].patchValue({ amount: '' });
      }
    });
  }

  townToChange(event: string, i: any) {
    if (event === '') return;
    this.distanceService.getDistance(this.lineItems.controls[i].get('from').value, event).subscribe({
      next: (data) => {
        this.lineItems.controls[i].patchValue({ factor: data.distance });
        this.calculateAmountForDistance(i)
      }
    })
  }

  calculateAmountForDistance(i: number): void {
    const lineItem = this.lineItems.at(i);
    const factor = lineItem.get('factor').value;
    const rate = lineItem.get('rate').value;
    const limit = lineItem.get('limit').value;

    let amount = factor * rate;
    if (limit != 0 && amount > limit) {
      amount = limit;
    }

    this.lineItems.controls[i].patchValue({ amount: amount });
    this.recalculateTotalAmount();
  }

  get lineItems(): FormArray {
    return this.requestForm.get('lineTypes') as FormArray;
  }

  addRow(): void {
    const emptyRow = this.fb.group({
      lineType: ['', Validators.required],
      description: [''],
      amount: ['', Validators.required],
      limit: [''],
      rate: [''],
      factor: [''],
      promptType: [''],
      promptText1: [''],
      promptText2: [''],
      promptText3: [''],
      from: [''],
      to: [''],
      townsTo: [[]]
    });
    this.lineItems.push(emptyRow);
    this.lineTypesTableDataSource.data = this.lineItems.controls;
  }
  
  removeRow(index: number): void {
    this.lineItems.removeAt(index);
    this.lineTypesTableDataSource.data = this.lineItems.controls;
  }

  lineTypeChanged(value: string, i: number): void {
    const selectedLineType = this.lineTypes.find(lineType => lineType.type === value);
    if (selectedLineType) {

      if (selectedLineType.promptType === 'DS') {
        this.getAllTowns()
      }

      const lineTypeFormGroup = this.lineItems.at(i) as FormGroup;
      lineTypeFormGroup.patchValue({
        lineType: selectedLineType.type,
        promptType: selectedLineType.promptType,
        rate: selectedLineType.rateDecimal,
        limit: selectedLineType.limitDecimal,
        promptText1: selectedLineType.promptText1,
        promptText2: selectedLineType.promptText2,
        promptText3: selectedLineType.promptText3
      });

      this.lineTypesTableDataSource.data = this.lineItems.controls;
    }
    this.recalculateTotalAmount();
  }

  loadDataForExistingRequest(): void {
    this.isLoading = true;
    forkJoin([this.enterRequestService.getPersonCompleteData(this.onBehalfOfEmail), this.enterRequestService.getAllRequestTypes()])
      .subscribe({
        next: ([personData, requestTypes]) => {
          if (personData) {
            this.personComplete = personData;
            if (this.personComplete.id !== 0) {
              this.addressBookSearchService.searchAddressBook(this.personComplete.id.toString()).subscribe({
                next: (data) => {
                  if (data && data.length > 0) {
                    this.payableTo = data[0];
                  }
                }
              });
            }
          }

          if (requestTypes) {
            this.requestTypesSource = requestTypes;
            this.filterRequestTypes();
          }
          this.isLoading = false;

          if (this.requestData.requestTypeId !== "") {
            this.initLineTypesFromImprest();
          }
        }
      });
  }

  loadDataForNewRequest(): void {
    this.isLoading = true;
    forkJoin([this.enterRequestService.getPersonCompleteData(this.onBehalfOfEmail), this.enterRequestService.getAllRequestTypes()])
      .subscribe({
        next: ([personData, requestTypes]) => {
          if (personData) {
            this.personComplete = personData;
            if (this.personComplete.id !== 0) {
              this.addressBookSearchService.searchAddressBook(this.personComplete.id.toString()).subscribe({
                next: (data) => {
                  if (data && data.length > 0) {
                    this.payableTo = data[0];
                  }
                }
              });
            }
          }
          if (requestTypes) {
            this.requestTypesSource = requestTypes;
            this.filterRequestTypes();
          }
          this.isLoading = false;
        }
      });
  }

  initLineTypesFromImprest() {
    this.isLineTypeLoading = true;
    let skipBenefitBasedLineTypes = true;
    if (this.onBehalfOfEmail || this.user.userRole === 'BP') {
      skipBenefitBasedLineTypes = false;
    }

    this.enterRequestService.getLineTypes(this.personComplete.id, this.requestData.requestTypeId, this.requestForm.value.mode, skipBenefitBasedLineTypes).subscribe({
      next: (data) => {
        if (data && data.length > 0) {
          this.lineTypes = data;

          this.imprestDetails.forEach(imprestDetail => {
            let lineTypeGroup;

            if (imprestDetail.promptType === 'D' || imprestDetail.promptType === 'F') {
              lineTypeGroup = this.fb.group({
                lineType: [imprestDetail.lineType],
                description: [imprestDetail.description],
                promptType: [imprestDetail.promptType],
                amount: [imprestDetail.amount],
                factor: [imprestDetail.factor],
                limit: [imprestDetail.limit]
              });
            } else if (imprestDetail.promptType === 'N') {
              lineTypeGroup = this.fb.group({
                lineType: [imprestDetail.lineType],
                promptType: [imprestDetail.promptType],
                promptText1: [imprestDetail.promptText1],
                factor: [imprestDetail.factor],
                amount: [imprestDetail.amount],
                limit: [imprestDetail.limit]
              });
            } else if (imprestDetail.promptType === 'DS' || imprestDetail.promptType === 'DD') {
              lineTypeGroup = this.fb.group({
                lineType: [imprestDetail.lineType],
                promptType: [imprestDetail.promptType],
                promptText1: [imprestDetail.promptText1],
                promptText2: [imprestDetail.promptText2],
                promptText3: [imprestDetail.promptText3],
                from: [imprestDetail.from],
                to: [imprestDetail.to],
                factor: [imprestDetail.factor],
                amount: [imprestDetail.amount],
                limit: [imprestDetail.limit],
                rate: [imprestDetail.rate],
                townsTo: [[imprestDetail.to]]

              });
            }
            this.lineItems.push(lineTypeGroup);
          });
          this.lineTypesTableDataSource.data = this.lineItems.controls;

          this.imprestDetails.forEach(imprestDetail => {
            this.lineTypeChanged(imprestDetail.lineTypeId, imprestDetail.line - 1)
          })
          this.isLineTypeLoading = false;
        }
      },
      error: (error) => {
        console.error(error);
      }
    })
  }

  filterRequestTypes(): void {
    this.requestTypes = this.requestTypesSource.filter(rt => {
      return ((rt.forEmployeeType === this.personComplete.employeeTypeID?.toString()) ||
        (rt.forEmployeeType === '0') ||
        (rt.forEmployeeType === this.personComplete.employeeTypeID?.toString()[0])) &&
        (rt.forMode === 'B' || rt.forMode === this.requestForm.value.mode);
    });
    this.requestForm.patchValue({ requestType: this.requestTypes[0]?.requestType });
    this.loadLineTypes(this.requestTypes[0]?.requestType);
  }

  loadLineTypes(selectedRequestType: string): void {
    this.isLineTypeLoading = true;
    let skipBenefitBasedLineTypes = true;
    if (this.onBehalfOfEmail || this.user.userRole === 'BP') {
      skipBenefitBasedLineTypes = false;
    }

    if (this.personComplete?.id === 0) {
      //person data doesn't exist
      this.isLineTypeLoading = false;
      return
    }
    this.lineItems.clear();
    this.lineTypes = [];
    this.enterRequestService.getLineTypes(this.personComplete.id, selectedRequestType, this.requestForm.value.mode, skipBenefitBasedLineTypes).subscribe({
      next: (data) => {        
        if (data && data.length > 0) {
          this.lineTypes = data;
          this.clearLineTypesTable();
        }
        this.isLineTypeLoading = false;
      },
      error: (error) => {
        console.error(error);
      }
    })
  }

  getRequestType(type: string): IRequestType {
    return this.requestTypes.find(rt => rt.requestType === type);
  }

  getLineType(type: string): ILineType {
    return this.lineTypes.find(lt => lt.type === type);
  }

  clearLineTypesTable(): void {
    if (this.lineItems.controls.length > 0) {
      this.lineItems.clear();
    }
    this.addRow();
  }

  recalculateTotalAmount(): void {
    this.totalAmount = this.lineItems.controls.reduce((sum, control) => {
      const amount: number = parseFloat(control.get('amount').value);
      return sum + (isFinite(amount) ? amount : 0);
    }, 0);

    this.totalAmount = Math.round(this.totalAmount * 100) / 100;
    this.totalAmountWords = toWords(this.totalAmount);
  }

  onBehalfOfSelected(person: IPerson): void {
    this.onBehalfOfName = person.fullName;
    this.onBehalfOfEmail = person.email;
    if (this.isEdit) {
      this.loadDataForExistingRequest();
    } else {
      this.loadDataForNewRequest();
    }
  }

  onApproverSelected(person: IPerson): void {
    this.approver = person;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const filesArray = Array.from(input.files);
      this.selectedFiles = this.selectedFiles.concat(filesArray).slice(0, 3); // Limit to 3 files
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  actionRequest(action: EnterRequestAction): void {
    this.action = action;

    if (this.action === this.ACTION.SaveRequest) {
      this.submitRequest(this.action);
    }
  }

  submit(): void {
    switch (this.action) {
      case this.ACTION.SaveRequest:
        this.submitRequest(this.action);
        break;
      case this.ACTION.SubmitRequest:
        if (this.comment === '') {
          this.errorResponse = "Please enter a comment.";
          break;
        }
        if (this.approver?.email === '' || this.approver?.email === undefined) {
          this.errorResponse = "Please select an approver.";
          break;
        }
        this.submitRequest(this.action);
        break;
      case this.ACTION.CancelRequest:
        this.cancelRequest();
        break;
    }
  }

  cancel(): void {
    this.action = this.ACTION.None;
  }

  cancelRequest(): void {
    const requestCancel: IRequestTerminate = {
      requestNumber: this.requestData.requestNumber,
      comment: this.comment,
      userEmail: this.user.userEmail,
      userName: this.user.userName,
      enteredByEmail: this.requestData.enteredByEmail
    };

    this.requestSubmitted = true;
    this.searchRequestService.cancelRequest(requestCancel).subscribe({
      next: () => {
        this.successResponse = "Request has been successfully canceled.";
      },
      error: () => {
      },
      complete: () => {
        this.submissionInProgress = false;
      }
    });
  }

  refresh(): void {
    window.location.reload();
  }

  downloadFile(file: File): void {
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

  }

  private collectValidationErrors(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.collectValidationErrors(control);
      } else if (control && control.invalid) {
        const errors = control.errors;
        if (errors) {
          Object.keys(errors).forEach(errorKey => {
            if (key === "requestTitle") {
              key = "Request Title";
            }
            if (key === "requestType") {
              key = "Request Type";
            }
            if (key === "mode") {
              key = "Mode";
            }
            if (key === "lineType") {
              key = "Line Type";
            }
            if (key === "chargeAfeCc") {
              key = "Charge AFE/CC";
            }
            if (key === "inRegardOf") {
              key = "In Regard Of";
            }
            this.validationErrors.push(`${key} is ${errorKey}.`);
          });
        }
      }
    });
  }

  clearMessages(): void {
    this.successResponse = undefined;
    this.errorResponse = undefined;
  }  

  searchPayableTo(): void {
    const dialogRef = this.dialog.open(SearchAddressBookEntryComponent, {
      data: {
        payableName: this.payableTo.name
      }
    });

    dialogRef.afterClosed().subscribe(data => {
      this.payableTo = data;
    });
  }

  submitRequest(action: EnterRequestAction): void {
    this.validationErrors = [];
    if (this.requestForm.invalid) {
      this.collectValidationErrors(this.requestForm);
      return;
    }

    if (this.selectedChargeAfeCc === undefined || this.selectedChargeAfeCc === null) {
      this.validationErrors.push("Invalid Charge AFE/CC");
      return;
    } else if (this.selectedChargeAfeCc.isClosed) {
      this.validationErrors.push("Charge AFE/CC is closed");
      return;
    }

    const request: IEnterRequest = {
      userName: this.user.userName,
      userEmail: this.user.userEmail,
      employeeName: this.personComplete.fullName,
      employeeEmail: this.personComplete.email,
      employeeNo: this.personComplete.id,
      employeeType: this.personComplete.employeeTypeID.toString(),
      payableToNo: this.payableTo.serialNumber,
      approver: this.approver.email,
      requestNumber: this.requestData?.requestNumber ? this.requestData.requestNumber : this.enterRequestResponse?.requestNumber ? this.enterRequestResponse.requestNumber : 0,
      requestStatus: this.action === this.ACTION.SaveRequest ? '00' : '10',
      approverName: this.approver.fullName,
      approverEmail: this.approver.email,
      requestTitle: this.requestForm.value.requestTitle,
      chargeAfeCc: this.requestForm.value.chargeAfeCc,
      jobTitle: this.personComplete.jobTitle,
      costCenter: this.personComplete.costCenter,
      divisionId: this.personComplete.divisionId,
      division: this.personComplete.division,
      locationId: this.personComplete.locationId,
      location: this.personComplete.location,
      requestType: this.requestForm.value.requestType,
      mode: this.requestForm.value.mode,
      inRegardOf: this.requestForm.value.inRegardOf,
      howPaid: this.getRequestType(this.requestForm.value.requestType).howPaid,
      confidential: false,
      totalAmount: this.totalAmount,
      attachments: this.selectedFiles,
      comment: this.comment,
      requestDetails: this.lineItems.controls.map(control => {
        return {
          lineType: this.getLineType(control.get('lineType').value),
          amount: control.get('amount').value,
          accountNumber: "0",
          accountId: "0",
          description: control.get('description')?.value,
          limit: control.get('limit')?.value,
          rate: control.get('rate')?.value,
          factor: control.get('factor')?.value || 0,
          from: control.get('from')?.value,
          to: control.get('to')?.value
        }
      })
    };

    this.requestSubmitted = true;
    this.enterRequestService.enterRequest(request, action).subscribe({
      next: (data: EnterRequestResponse) => {
        if (data.resultInfo.isSuccess) {
          this.successResponse = `Success: ${data.messages.join(' ')}`;
          this.enterRequestResponse = data;
        } else {
          this.errorResponse = data.resultInfo.error.description;
        }
      },
      error: (error) => {
        this.submissionInProgress = false;
        this.errorResponse = error;
      },
      complete: () => {
        this.submissionInProgress = false;
      }
    });
  }
}
